/* eslint-disable jira/integration/test-id-by-folder-structure */
/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useEffect, useCallback, useRef } from 'react';
import Heading from '@atlaskit/heading/heading';
import { xcss } from '@atlaskit/primitives/xcss';
import Inline from '@atlaskit/primitives/inline';
import Stack from '@atlaskit/primitives/stack';
import Text from '@atlaskit/primitives/text';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

import { ModalEntryPointDropdownItemTrigger } from '@jira/platform__entry-points-modal-entry-point-dropdown-item-trigger/src/ModalEntryPointDropdownItemTrigger.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { BOARD_TYPES } from './boardTypes.constants.tsx';
import { boardCreateModalEntryPoint } from './boardCreateModalEntryPoint.entrypoint.tsx';
import { BoardCreateModalFallback } from './MemoizedBoardCreateModalWithContext.tsx';
import type { DropdownItemContentProps } from './DropdownItemContentProps.types.tsx';

const createBoardLabel = defineMessage({
	id: 'software-board-create.flyout-menu.flyout-menu-content.dropdown-item-container.create-board-label',
	defaultMessage: 'Create a {boardType} board',
	description:
		"The aria-label for the dropdown item trigger that opens the board creation modal. The {boardType} is either 'scrum' or 'kanban'.",
});

export const DropdownItemContainer = ({
	entryPointProps,
	title,
	description,
	icon,
	index,
}: DropdownItemContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const triggerRef = useRef<HTMLElement>(null);

	useEffect(() => {
		if (index === 0 && triggerRef.current) {
			triggerRef.current.focus();
		}
	}, [index]);

	const handleOnDropdownItemClick = useCallback(() => {
		const boardTypePascalCase =
			entryPointProps.boardType === BOARD_TYPES.SCRUM ? 'Scrum' : 'Kanban';
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'dropdownItem',
				action: 'clicked',
			}),
			`create${boardTypePascalCase}Board`,
		);
	}, [createAnalyticsEvent, entryPointProps.boardType]);

	return (
		<ModalEntryPointDropdownItemTrigger
			aria-label={formatMessage(createBoardLabel, {
				boardType: entryPointProps.boardType.toLowerCase(),
			})}
			forwardedRef={triggerRef}
			entryPoint={boardCreateModalEntryPoint}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="software-board-create.ui.flyout-menu.flyout-menu-content.dropdown-item-container.dropdown-item-trigger"
			interactionName="open-create-board-modal"
			entryPointProps={entryPointProps}
			Fallback={BoardCreateModalFallback}
			onClick={handleOnDropdownItemClick}
			useInternalModal={false}
		>
			<Inline space="space.100" alignBlock="start">
				<Inline alignBlock="center" alignInline="center" xcss={itemIconBackgroundStyles}>
					{icon}
				</Inline>
				<Stack>
					<Heading size="xsmall" as="h3">
						{title}
					</Heading>
					<Text size="medium">{description}</Text>
				</Stack>
			</Inline>
		</ModalEntryPointDropdownItemTrigger>
	);
};

const ICON_BACKGROUND_CONTAINER_SIZE = '32px';

const itemIconBackgroundStyles = xcss({
	backgroundColor: 'color.background.neutral',
	width: ICON_BACKGROUND_CONTAINER_SIZE,
	height: ICON_BACKGROUND_CONTAINER_SIZE,

	borderRadius: token('radius.xsmall', '3px'),
	padding: 'space.050',
	flexShrink: 0,
});
